import React from 'react';
import { motion } from 'framer-motion';
import { Search, Calendar, User, ArrowRight, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const Research: React.FC = () => {
  const navigate = useNavigate();
  const posts = [
    {
      title: "Deep Dive: Analyzing the Euler Finance Exploit",
      date: "March 15, 2026",
      author: "Gabriel Nworah",
      category: "Security Analysis",
      desc: "A step-by-step visual trace analysis of the complex reentrancy exploit that led to the Euler Finance hack."
    },
    {
      title: "Optimizing Gas Consumption in Layer 2 Protocols",
      date: "February 28, 2026",
      author: "Oru Research Team",
      category: "Gas Analysis",
      desc: "How visual debugging helped identify redundant storage operations in a major L2 bridge contract."
    },
    {
      title: "The Future of EVM Debugging: Visual Execution Paths",
      date: "January 12, 2026",
      author: "Oru Labs",
      category: "Product Vision",
      desc: "Why traditional CLI debuggers are no longer sufficient for the increasingly complex world of DeFi."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-32 pb-20 px-8">
      <div className="max-w-7xl mx-auto">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-[#525252] hover:text-white transition-colors mb-8 group"
        >
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
          Back to Overview
        </motion.button>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-20"
        >
          <h1 className="text-5xl md:text-7xl font-bold tracking-tighter text-white mb-6 uppercase">
            RESEARCH & <span className="text-emerald-500">INSIGHTS</span>
          </h1>
          <p className="text-[#525252] font-mono text-lg max-w-2xl leading-relaxed">
            Deep dives into transaction behavior, security case studies, and gas analysis reports from the Oru Labs team.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="group flex flex-col bg-[#0F0F0F] border border-[#262626] rounded-3xl overflow-hidden hover:border-emerald-500/30 transition-all"
            >
              <div className="aspect-video bg-[#1A1A1A] relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-transparent opacity-50" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Search className="w-12 h-12 text-[#262626] group-hover:text-emerald-500/50 transition-colors" />
                </div>
              </div>
              <div className="p-8 flex flex-col flex-1">
                <div className="flex items-center gap-4 mb-6">
                  <span className="px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/20 text-[9px] font-bold uppercase tracking-widest text-emerald-500">
                    {post.category}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-emerald-500 transition-colors leading-tight">
                  {post.title}
                </h3>
                <p className="text-[#525252] text-sm leading-relaxed mb-8 flex-1">
                  {post.desc}
                </p>
                <div className="flex items-center justify-between pt-6 border-t border-[#262626]">
                  <div className="flex items-center gap-4 text-[10px] font-mono text-[#404040]">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" />
                      {post.date}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <User className="w-3 h-3" />
                      {post.author}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#404040] group-hover:text-emerald-500 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};
